import React from 'react';

/**
 * THREE DELIVERABLES PER SCREEN, NOT ONE.
 *
 * An overlay and a full-frame plate are genuinely different assets, and so are
 * a vertical plate and a landscape one — a creator either drops the product
 * over their own footage, or cuts to a full-screen shot of it. Handing over
 * only the square meant everyone rebuilt the full-frame version by hand.
 *
 * So each screen ships three ways, and the toggle on the page picks which one
 * you are exporting, not just which one you are looking at:
 *
 *   Overlay   1080×1080  square, transparent — drops on any timeline
 *   9:16      1080×1920  full frame, screen high, caption band beneath
 *   16:9      1920×1080  full frame, screen centred
 *
 * The two plates are built by placing the SAME square artboard on brand
 * ground, so a screen is composed once and framed three ways. Nothing can
 * drift between them, and the plates cannot overflow.
 *
 * WHY THE SCREEN IS SCALED RATHER THAN RE-LAID-OUT. These are real product
 * screens whose breakpoints watch the VIEWPORT, not their container, so
 * narrowing the box does not reflow them — it squashes them. Scaling the whole
 * screen as one object keeps every internal proportion, hairline and type
 * relationship exactly as designed. It gets smaller; it does not get wrong.
 */

export type PlateRatio = '9:16' | '16:9';
export type PlateView = 'overlay' | PlateRatio;

/** Half of the delivered size, so a 2× PNG lands on the stated pixel size. */
export const PLATE_SIZE = 540;
export const VIEW_SIZES: Record<PlateView, {
  w: number;
  h: number;
  out: string;
  label: string;
}> = {
  overlay: {
    w: PLATE_SIZE,
    h: PLATE_SIZE,
    out: '1080×1080',
    label: 'Overlay'
  },
  '9:16': {
    w: 540,
    h: 960,
    out: '1080×1920',
    label: '9:16'
  },
  '16:9': {
    w: 960,
    h: 540,
    out: '1920×1080',
    label: '16:9'
  }
};

/**
 * The plates' ground — a defocused, cinematic interior, baked into the export.
 *
 * Deliberately abstract: no people, nothing in focus, no readable objects. A
 * plate with a stock person in it is a plate that only fits one story, and it
 * dates the moment the actor's clothes do. Blurred warm bokeh reads as "this
 * is a frame of video" without claiming anything about whose video it is, and
 * it gives the light UI panel something dark to sit on.
 */
export const BACKDROP: Record<PlateRatio, string> = {
  '9:16': 'https://cdn.magicpatterns.com/patterns/generated-images/6c381cf4-1f03-4270-810f-25f45cdd34ce.jpg',
  '16:9': 'https://cdn.magicpatterns.com/patterns/generated-images/97bdd8f1-ea71-4314-b86b-7b91c8fbb9aa.jpg'
};

/**
 * THE SQUARE ARTBOARD — the overlay asset, and the unit both plates are built
 * from.
 *
 * No fill and no grid: anything painted here becomes a rectangle the creator
 * has to crop or key out. The checkerboard on the page is the page's, not the
 * asset's.
 */
export function PlateArtboard({
  children,
  /** Natural CSS width the screen inside is composed at. */
  width,
  /**
   * Width the screen renders at on the artboard. 470 of 540 leaves room for
   * the screen's own drop shadow to fall inside the file rather than clip.
   */
  renderWidth = 470,
  /**
   * CLOSE crops in instead of fitting the whole screen. The fitted overlay is
   * the safe default, but at the size an overlay is actually placed — a
   * quarter of a phone screen — a whole app view is decorative rather than
   * legible. Close renders the screen half again as large and lets the square
   * clip it, so the part in frame can be read.
   */
  zoom = 1
}: {
  children: React.ReactNode;
  width: number;
  renderWidth?: number;
  zoom?: number;
}) {
  // `overflow-hidden` makes the square a real crop boundary: a band composed
  // to run past the edge is clipped by the file rather than bleeding outside.
  return <div className="relative flex items-center justify-center overflow-hidden" style={{
    width: PLATE_SIZE,
    height: PLATE_SIZE
  }} data-id="element-3254" data-mp="1z75w2noc5l5o">
      {/* Centred by FLEX, then scaled about its own centre. Layout measures the
       child at its pre-transform size, so this centres correctly without the
       artboard ever needing to know how tall the screen is. */}
      <div className="flex-none" style={{
      width,
      transform: `scale(${renderWidth * zoom / width})`
    }} data-id="element-3255" data-mp="16inxdjn9liz8">
        {children}
      </div>
    </div>;
}

/**
 * A CROP OF A SCREEN, at a size a phone can actually read.
 *
 * The vertical plate used to shrink a whole 900–1240px desktop screen to fit
 * a 486px column — a third of its drawn size, which on a phone held at arm's
 * length is a grey blur where the labels should be. Showing everything at an
 * unreadable size communicates less than showing part of it clearly.
 *
 * So 9:16 no longer scales the screen down; it CROPS IN. The screen renders
 * near its true size and the frame shows the column that carries the point —
 * the thread, the connection rows, the run history. Everything to the right of
 * the crop is the part nobody could read anyway.
 *
 * The window's height is measured from the content rather than fixed, so a
 * short screen is not padded out with empty ground and a tall one is cut at a
 * chosen line instead of wherever the frame happened to end.
 */
function PlateCrop({
  children,
  width,
  zoom,
  x = 0,
  cw,
  maxH
}: {
  children: React.ReactNode;
  width: number;
  zoom: number;
  /** Natural px trimmed off the left, when the point sits off-centre. */
  x?: number;
  cw: number;
  maxH: number;
}) {
  const inner = React.useRef<HTMLDivElement>(null);
  const [natural, setNatural] = React.useState(0);
  React.useLayoutEffect(() => {
    // offsetHeight is the pre-transform layout height, which is exactly what
    // the scale maths needs.
    const h = inner.current?.offsetHeight ?? 0;
    // Guarded so an unchanged measurement cannot re-enter the effect.
    setNatural(prev => prev === h ? prev : h);
  });
  const height = natural ? Math.min(natural * zoom, maxH) : maxH;
  return <div className="relative overflow-hidden rounded-[14px] bg-white" style={{
    width: cw,
    height
  }} data-id="element-3256" data-mp="2jgxjqvgt8iwo">
      <div ref={inner} className="absolute left-0 top-0 origin-top-left" style={{
      width,
      transform: `translateX(${-x * zoom}px) scale(${zoom})`
    }} data-id="element-3257" data-mp="2ofqs69h8pjpz">
        {children}
      </div>
    </div>;
}

/**
 * A FULL-FRAME PLATE — the square, composited onto a video-looking ground.
 *
 * Every asset here is a STILL PNG; there is no GIF or video export and none is
 * planned. So a full-frame plate has to look like a frame OF video on its own,
 * which the earlier cream-and-grid ground did not — it looked like a slide.
 * The screen now sits on a defocused interior with a real drop shadow, and the
 * result cuts straight into a timeline.
 *
 * Vertical reserves a deep band at the bottom and rides the screen high, since
 * vertical captions sit below. Landscape simply centres it, because landscape
 * captions sit beside or over the picture and a reserved band would strand it
 * against the top edge.
 */
export type VerticalCrop = {
  /** Scale the screen renders at inside the vertical crop. */
  zoom: number;
  /** Natural px trimmed off the left, when the point sits off-centre. */
  x?: number;
};
export function PlatePreview({
  children,
  width,
  renderWidth,
  ratio,
  crop,
  centered
}: {
  children: React.ReactNode;
  width: number;
  renderWidth?: number;
  ratio: PlateRatio;
  crop?: VerticalCrop;
  /**
   * Centre the square in the vertical frame instead of riding it high.
   *
   * The high position exists to reserve a caption band under a tall app
   * screen. A single small object — a prompt, a settings card — does not fill
   * the frame, so riding it high leaves a large void beneath it and the plate
   * reads as top-heavy rather than as composed.
   */
  centered?: boolean;
}) {
  const {
    w,
    h
  } = VIEW_SIZES[ratio];
  const vertical = ratio === '9:16';
  // How much of the frame the square occupies, and where it sits.
  const size = vertical ? w * 0.94 : h * 0.84;
  // Vertical sits slightly above centre rather than pinned to the top: a plate
  // is captioned from below in an editor, so a touch more ground underneath is
  // useful — but the old 7%-from-top left a void, not a caption zone.
  const pos = vertical ? {
    left: (w - size) / 2,
    top: centered ? (h - size) / 2 : (h - size) * 0.42
  } : {
    left: (w - size) / 2,
    top: (h - size) / 2
  };
  return <div className="relative overflow-hidden" style={{
    width: w,
    height: h,
    backgroundColor: '#14100D'
  }} data-id="element-3258" data-mp="2nkwvi8nv09q1">
      <img src={BACKDROP[ratio]} alt="" aria-hidden="true" className="absolute inset-0 size-full object-cover" data-id="element-3259" data-mp="2siyeyup76b21" />

      {/* A touch of extra darkness at the caption end, so white burned-in text
       has something to sit on wherever the backdrop happens to be bright. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
      background: vertical ? 'linear-gradient(to bottom, rgba(20,16,13,0) 55%, rgba(20,16,13,0.55) 100%)' : 'linear-gradient(to bottom, rgba(20,16,13,0) 62%, rgba(20,16,13,0.5) 100%)'
    }} data-id="element-3260" data-mp="24sb543vq1bcu" />

      {/* The screen, lifted off the backdrop by a real shadow — without one it
       reads as pasted on rather than composited in. */}
      {/* CENTRED, not ridden high. The crop window is measured from its own
       content, so it is rarely tall enough to need a reserved caption band
       beneath it — pinning it near the top just left a large void under a
       short crop and made the plate read as top-heavy. Centring keeps the
       remaining ground split evenly above and below. */}
      {vertical && crop ? <div className="absolute inset-0 flex items-center justify-center" style={{
      filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.45))'
    }} data-id="element-3261" data-mp="19kse7kx1bb2g">
          <PlateCrop width={width} zoom={crop.zoom} x={crop.x} cw={w * 0.9} maxH={h * 0.62} data-id="element-3262" data-mp="zlteiru8yprl">
            {children}
          </PlateCrop>
        </div> : <div className="absolute" style={{
      width: size,
      height: size,
      ...pos,
      filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.45))'
    }} data-id="element-3263" data-mp="vliysngnkkzj">
          <div className="origin-top-left" style={{
        transform: `scale(${size / PLATE_SIZE})`
      }} data-id="element-3264" data-mp="23z1ns138490p">
            <PlateArtboard width={width} renderWidth={renderWidth} data-id="element-3265" data-mp="2io675llr8jae">
              {children}
            </PlateArtboard>
          </div>
        </div>}
    </div>;
}